import { FormEvent, ReactNode, useEffect, useState } from 'react'
import { CompanySetup } from './CompanySetup'
import { api } from './api'
import { businessStatus, messageFor, Notice, roleLabel, StatusPill } from './ui'
import { SystemLayout } from './SystemAdmins'
import { SystemLink } from './SystemOverview'
import './system-overview.css'

type Tenant = { id: string; slug: string; name: string; description: string; logo_url?: string; status: string; self_service_account_creation: boolean }
type Person = { id: string; tenant_id: string; display_name: string; email: string; role: string; status: string }
type App = { id: string; tenant_id?: string; name: string; description?: string; tagline?: string; catalog_label?: string; launch_url?: string; launch_protocol: string; logo_url?: string; client_type: string; status: string; redirect_uris: string[]; scopes: string[] }
type Directory = {
  tenants: Tenant[]
  people: Person[]
  clients: App[]
  domains: Array<{ id: string; tenant_id: string; domain: string }>
  connections: Array<{ id: string; tenant_id: string; name: string; protocol: string; status: string }>
  groups: Array<{ tenant_id: string; id: string; display_name: string; member_count: number }>
}

const request = <T,>(path: string, init: RequestInit = {}) => api<T>(path, { ...init, signal: AbortSignal.timeout(20000) })

const PAGE = 100

function useDirectory() {
  const [data, setData] = useState<Directory | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(true)
  // Edit controls appear only when the session positively says superadmin.
  // A failed or ambiguous lookup hides them; the server enforces either way.
  const [canEdit, setCanEdit] = useState(false)
  const load = async () => {
    setBusy(true)
    setError('')
    try { setData(await request<Directory>('/api/v1/operator/overview')) } catch { setError('We could not load this list. Try again.') } finally { setBusy(false) }
  }
  useEffect(() => {
    void load()
    request<{ capabilities?: { system_operator_role?: string } }>('/api/v1/session')
      .then((session) => setCanEdit(session.capabilities?.system_operator_role === 'superadmin'))
      .catch(() => setCanEdit(false))
  }, [])
  return { data, error, busy, load, canEdit }
}

function useShown<T>(rows: T[], reset: unknown) {
  const [limit, setLimit] = useState(PAGE)
  useEffect(() => { setLimit(PAGE) }, [reset])
  const more = rows.length > limit ? <button className="button secondary system-more" onClick={() => setLimit(limit + PAGE)}>Show {Math.min(PAGE, rows.length - limit)} more of {rows.length - limit} remaining</button> : null
  return [rows.slice(0, limit), more] as const
}

const matches = (query: string, ...values: Array<string | undefined>) => {
  const needle = query.trim().toLowerCase()
  return !needle || values.some((value) => value?.toLowerCase().includes(needle))
}

function DirectoryPage({ active, title, description, state, children }: { active: 'companies' | 'people' | 'apps' | 'company-setup'; title: string; description: string; state: ReturnType<typeof useDirectory>; children: (data: Directory) => ReactNode }) {
  return <SystemLayout active={active}>
    <div className="page-heading">
      <div><h1>{title}</h1><p>{description}</p></div>
      <button className="button secondary" disabled={state.busy} onClick={() => { void state.load() }}>Refresh</button>
    </div>
    {state.error && <><Notice tone="error">{state.error}</Notice><button className="button secondary" disabled={state.busy} onClick={() => { void state.load() }}>Try again</button></>}
    {state.busy && !state.data && <p role="status">Loading…</p>}
    {state.data && children(state.data)}
  </SystemLayout>
}

function Search({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="system-search">{label}<input type="search" value={value} onChange={(event) => onChange(event.target.value)} /></label>
}

const companyName = (data: Directory, id?: string) => data.tenants.find((tenant) => tenant.id === id)?.name ?? 'No company'
const activeLabel = (status: string) => status === 'active' ? 'Active' : businessStatus(status)

export function SystemCompanies() {
  const state = useDirectory()
  const [query, setQuery] = useState('')
  const data = state.data
  const [rows, more] = useShown(data ? data.tenants.filter((tenant) => matches(query, tenant.name, tenant.slug, ...data.domains.filter((d) => d.tenant_id === tenant.id).map((d) => d.domain))) : [], query)
  return <DirectoryPage active="companies" title="Companies" description="Every company that signs in through this AuthEM system." state={state}>{(data) => {
    return <section className="section-card">
      <Search label="Search companies" value={query} onChange={setQuery} />
      {rows.length === 0 ? <p>No companies match.</p> : <div className="table-scroll"><table>
        <thead><tr><th>Company</th><th>Work email domains</th><th>People</th><th>Apps</th><th>Status</th></tr></thead>
        <tbody>{rows.map((tenant) => <tr key={tenant.id}>
          <td><SystemLink href={`/system/companies/${encodeURIComponent(tenant.id)}`}>{tenant.name}</SystemLink></td>
          <td>{data.domains.filter((d) => d.tenant_id === tenant.id).map((d) => d.domain).join(', ') || '—'}</td>
          <td>{data.people.filter((p) => p.tenant_id === tenant.id).length}</td>
          <td>{data.clients.filter((c) => c.tenant_id === tenant.id).length}</td>
          <td><StatusPill value={activeLabel(tenant.status)} /></td>
        </tr>)}</tbody>
      </table></div>}
      {more}
    </section>
  }}</DirectoryPage>
}

export function SystemCompany({ id }: { id: string }) {
  const state = useDirectory()
  const tenant = state.data?.tenants.find((item) => item.id === id)
  return <DirectoryPage active="companies" title={tenant?.name ?? 'Company'} description={tenant ? `Signs in at /c/${tenant.slug}/login` : 'Company details'} state={state}>{(data) => {
    if (!tenant) return <section className="section-card"><p>This company was not found.</p><SystemLink href="/system/companies">Back to companies</SystemLink></section>
    const people = data.people.filter((p) => p.tenant_id === id)
    const apps = data.clients.filter((c) => c.tenant_id === id)
    const domains = data.domains.filter((d) => d.tenant_id === id)
    const connections = data.connections.filter((c) => c.tenant_id === id)
    const groups = data.groups.filter((g) => g.tenant_id === id)
    return <>
      <section className="section-card">
        <h2>Sign-in</h2>
        <dl className="system-facts">
          <div><dt>Status</dt><dd><StatusPill value={activeLabel(tenant.status)} /></dd></div>
          <div><dt>Work email domains</dt><dd>{domains.map((d) => d.domain).join(', ') || 'None approved'}</dd></div>
          <div><dt>Company sign-in</dt><dd>{connections.map((c) => `${c.name} (${c.protocol.toUpperCase()}${c.status === 'active' ? '' : `, ${businessStatus(c.status)}`})`).join(', ') || 'AuthEM accounts only'}</dd></div>
          <div><dt>New accounts</dt><dd>{tenant.self_service_account_creation ? 'People can create their own account' : 'Created by an admin'}</dd></div>
        </dl>
      </section>
      <section className="section-card">
        <h2>People ({people.length})</h2>
        {people.length === 0 ? <p>No people yet.</p> : <div className="table-scroll"><table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th></tr></thead>
          <tbody>{people.map((p) => <tr key={p.id}><td>{p.display_name || 'Unnamed'}</td><td>{p.email || '—'}</td><td>{roleLabel(p.role, p.status)}</td></tr>)}</tbody>
        </table></div>}
      </section>
      <section className="section-card">
        <div className="system-section-heading"><h2>Apps ({apps.length})</h2><SystemLink href="/system/apps">All apps</SystemLink></div>
        {apps.length === 0 ? <p>No apps yet.</p> : <ul className="system-recent">{apps.map((app) => <li key={app.id}><span>{app.name}</span><span className="status-pill">{app.launch_protocol.toUpperCase()}</span><span>{activeLabel(app.status)}</span></li>)}</ul>}
      </section>
      {groups.length > 0 && <section className="section-card">
        <h2>Groups</h2>
        <ul className="system-recent">{groups.map((g) => <li key={g.id}><span>{g.display_name}</span><span /><span>{g.member_count} {g.member_count === 1 ? 'member' : 'members'}</span></li>)}</ul>
      </section>}
      <section className="section-card"><div className="system-section-heading"><h2>Company setup</h2><SystemLink href={`/system/company-setup?company=${encodeURIComponent(tenant.id)}`}>Open company setup</SystemLink></div><p>App registration and secrets, company sign-in connections, and SCIM provisioning.</p></section>
    </>
  }}</DirectoryPage>
}

export function SystemPeople() {
  const state = useDirectory()
  const [query, setQuery] = useState('')
  const [company, setCompany] = useState('')
  const matching = state.data ? state.data.people.filter((p) => (!company || p.tenant_id === company) && matches(query, p.display_name, p.email)) : []
  const [rows, more] = useShown(matching, `${query}|${company}`)
  return <DirectoryPage active="people" title="People" description="Everyone with a company membership. A person in two companies appears twice." state={state}>{(data) => {
    return <section className="section-card">
      <div className="system-filters">
        <Search label="Search by name or email" value={query} onChange={setQuery} />
        <label className="system-search">Company<select value={company} onChange={(event) => setCompany(event.target.value)}><option value="">All companies</option>{data.tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
      </div>
      <p className="section-description">{matching.length} of {data.people.length} memberships · {new Set(data.people.map((p) => p.id)).size} people</p>
      {rows.length === 0 ? <p>No people match.</p> : <div className="table-scroll"><table>
        <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Role</th></tr></thead>
        <tbody>{rows.map((p) => <tr key={`${p.id}:${p.tenant_id}`}>
          <td>{p.display_name || 'Unnamed'}</td><td>{p.email || '—'}</td>
          <td><SystemLink href={`/system/companies/${encodeURIComponent(p.tenant_id)}`}>{companyName(data, p.tenant_id)}</SystemLink></td>
          <td>{roleLabel(p.role, p.status)}</td>
        </tr>)}</tbody>
      </table></div>}
      {more}
    </section>
  }}</DirectoryPage>
}

function PresentationForm({ app, onSaved, onCancel }: { app: App; onSaved: () => Promise<void>; onCancel: () => void }) {
  const [fields, setFields] = useState({ name: app.name, tagline: app.tagline ?? '', description: app.description ?? '', launch_url: app.launch_url ?? '', catalog_label: app.catalog_label ?? '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const field = (key: keyof typeof fields, label: string) => <label>{label}<input value={fields[key]} disabled={busy} onChange={(event) => setFields({ ...fields, [key]: event.target.value })} /></label>
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await request(`/api/v1/admin/clients/${encodeURIComponent(app.id)}/presentation`, { method: 'PUT', body: JSON.stringify(fields) })
      await onSaved()
    } catch (reason) { setError(messageFor(reason)) } finally { setBusy(false) }
  }
  return <form className="company-settings-form system-app-form" onSubmit={(event) => { void submit(event) }}>
    {error && <Notice tone="error">{error}</Notice>}
    {field('name', 'Name')}{field('tagline', 'Tagline')}{field('description', 'Description')}{field('launch_url', 'Launch URL')}{field('catalog_label', 'Catalog label')}
    <div className="system-form-actions"><button className="button primary" disabled={busy || !fields.name.trim()}>{busy ? 'Saving…' : 'Save'}</button><button type="button" className="button secondary" disabled={busy} onClick={onCancel}>Cancel</button></div>
  </form>
}

export function SystemApps() {
  const state = useDirectory()
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState('')
  const [notice, setNotice] = useState('')
  const data = state.data
  const [rows, more] = useShown(data ? data.clients.filter((app) => matches(query, app.name, app.id, companyName(data, app.tenant_id), app.launch_url)) : [], query)
  return <DirectoryPage active="apps" title="Apps" description="Applications that sign people in through AuthEM. Add an app, or change its sign-in settings and secrets, under Company setup." state={state}>{(data) => {
    return <>
      {notice && <Notice tone="success">{notice}</Notice>}
      <section className="section-card"><Search label="Search apps" value={query} onChange={setQuery} /></section>
      {rows.length === 0 ? <section className="section-card"><p>No apps match.</p></section> : rows.map((app) => <section className="section-card system-app" key={app.id}>
        <div className="system-section-heading">
          <div className="system-app-title">{app.logo_url && <img src={app.logo_url} alt="" width={36} height={36} />}<div><h2>{app.name}</h2><p className="section-description">{companyName(data, app.tenant_id)} · <code>{app.id}</code></p></div></div>
          <StatusPill value={activeLabel(app.status)} />
        </div>
        {app.tagline && <p>{app.tagline}</p>}
        <dl className="system-facts">
          <div><dt>Sign-in</dt><dd>{app.launch_protocol.toUpperCase()} · {app.client_type === 'confidential' ? 'Server app (has a secret)' : 'Public app (no secret)'}</dd></div>
          <div><dt>Launch URL</dt><dd>{app.launch_url || '—'}</dd></div>
          <div><dt>Permissions requested</dt><dd>{app.scopes.join(', ') || '—'}</dd></div>
        </dl>
        {app.redirect_uris.length > 0 && <details><summary>Return addresses ({app.redirect_uris.length})</summary><ul className="system-uri-list">{app.redirect_uris.map((uri) => <li key={uri}><code>{uri}</code></li>)}</ul></details>}
        {editing === app.id
          ? <PresentationForm app={app} onCancel={() => setEditing('')} onSaved={async () => { setEditing(''); setNotice(`${app.name} updated.`); await state.load() }} />
          : state.canEdit && <div className="row-actions"><button className="button secondary" onClick={() => { setNotice(''); setEditing(app.id) }}>Edit name and description</button><SystemLink href={`/system/company-setup?company=${encodeURIComponent(app.tenant_id ?? "")}`}>Sign-in settings and secrets</SystemLink></div>}
      </section>)}
      {more}
    </>
  }}</DirectoryPage>
}

/** System > Company setup: one company's app registration, sign-in
 * connections and SCIM, chosen with the picker (or ?company=). */
export function SystemCompanySetup() {
  const state = useDirectory()
  const [chosen, setChosen] = useState(() => new URLSearchParams(window.location.search).get('company') ?? '')
  return <DirectoryPage active="company-setup" title="Company setup" description="App registration and secrets, company sign-in connections, and SCIM provisioning for one company." state={state}>{(data) => {
    const companies = [...data.tenants].sort((a, b) => a.name.localeCompare(b.name))
    const id = companies.some((t) => t.id === chosen) ? chosen : companies[0]?.id ?? ''
    if (!id) return <section className="section-card"><p>No companies yet.</p></section>
    return <>
      <section className="section-card"><label className="system-search">Company<select value={id} onChange={(event) => { setChosen(event.target.value); window.history.replaceState({}, '', `/system/company-setup?company=${encodeURIComponent(event.target.value)}`) }}>{companies.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label></section>
      <CompanySetup key={id} companyID={id} />
    </>
  }}</DirectoryPage>
}
